import {
  hasRequiredArtifactPreviewForWorkflow,
  isContractedArtifactWorkflow,
  requiresArtifactPreviewContract,
} from "./assistantArtifactPromptRenderContract";
import type { AdmissionsAssistantArtifactPreview } from "./types";

type AssistantArtifactRefLike = {
  artifactType?: string | null;
  downloadUrl?: string | null;
  sourceRef?: string | null;
  fileName?: string | null;
};

type AssistantArtifactLongRunningWorkflowInput = {
  taskClass?: string | null;
  workflowKey?: string | null;
  state: string;
  phaseLabel?: string | null;
  statusMessage?: string | null;
  progressPercent?: number | null;
  resultSummary?: string | null;
  artifacts?: readonly AssistantArtifactRefLike[];
  response?: Record<string, unknown> | null;
  artifactPreview?: AdmissionsAssistantArtifactPreview | null;
};

export const ASSISTANT_ARTIFACT_HYDRATING_STATE = "hydrating_artifact" as const;

export const ASSISTANT_LONG_RUNNING_ARTIFACT_WORKFLOW_KEYS = new Set([
  "artifact.chart.generate",
  "artifact.visualization3d.generate",
  "artifact.spreadsheet.generate",
  "artifact.spreadsheet.modify",
  "artifact.landing_page.draft",
  "artifact.diagram.generate",
]);

export type AssistantLongRunningWorkflowThreadState =
  | "started"
  | "running"
  | typeof ASSISTANT_ARTIFACT_HYDRATING_STATE
  | "completed"
  | "failed"
  | "cancelled";

export type AssistantArtifactLongRunningWorkflowProjection = {
  state: string;
  phaseLabel: string | null;
  statusMessage: string | null;
  progressPercent: number | null;
  resultSummary: string | null;
  hasArtifactPayload: boolean;
  isArtifactWorkflow: boolean;
  isHydratingArtifact: boolean;
  isReady: boolean;
};

const toRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const normalizeState = (value: unknown): string => {
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();
  return normalized.length > 0 ? normalized : "queued";
};

const normalizeCopy = (value: string | null | undefined): string | null => {
  const normalized = String(value ?? "").trim();
  return normalized.length > 0 ? normalized : null;
};

const defaultPhaseLabel = (state: string): string =>
  state
    .split(/[_\s-]+/)
    .filter((token) => token.length > 0)
    .map((token) => token[0]!.toUpperCase() + token.slice(1))
    .join(" ") || "Queued";

const defaultProgress = (state: string): number => {
  switch (state) {
    case "queued":
      return 8;
    case "started":
      return 12;
    case "planning":
      return 16;
    case "opened":
      return 22;
    case "collecting_data":
      return 38;
    case "validating_scope":
      return 52;
    case "generating":
      return 72;
    case "packaging_artifact":
      return 88;
    case "waiting_for_confirmation":
      return 66;
    case "sending":
      return 82;
    case ASSISTANT_ARTIFACT_HYDRATING_STATE:
      return 96;
    case "completed":
    case "failed":
    case "cancelled":
      return 100;
    default:
      return 18;
  }
};

const clampProgress = (
  value: number | null | undefined,
  state: string,
): number => {
  if (!Number.isFinite(value)) {
    return defaultProgress(state);
  }
  return Math.max(0, Math.min(100, Number(value)));
};

const hasArtifactRef = (value: unknown): boolean => {
  const record = toRecord(value);
  if (!record) {
    return false;
  }
  return (
    typeof record.downloadUrl === "string" ||
    typeof record.sourceRef === "string" ||
    typeof record.fileName === "string"
  );
};

const hasArtifactArray = (value: unknown): boolean =>
  Array.isArray(value) && value.some((entry) => hasArtifactRef(entry));

const hasArtifactPayloadFromResponse = (
  response: Record<string, unknown> | null | undefined,
): boolean => {
  const debug = toRecord(response?.debug);
  const taskDebug = toRecord(debug?.task);
  if (!debug && !taskDebug) {
    return false;
  }
  return (
    hasArtifactArray(debug?.artifactRefs) ||
    hasArtifactRef(taskDebug?.workbookArtifactRef) ||
    hasArtifactRef(taskDebug?.downloadArtifactRef) ||
    hasArtifactRef(taskDebug?.chartArtifactRef) ||
    hasArtifactRef(taskDebug?.visualization3DArtifactRef) ||
    hasArtifactRef(taskDebug?.landingPageArtifactRef) ||
    hasArtifactRef(taskDebug?.diagramArtifactRef) ||
    (Array.isArray(taskDebug?.chartPanels) &&
      taskDebug.chartPanels.length > 0) ||
    (taskDebug?.visualization3DSpace != null &&
      typeof taskDebug.visualization3DSpace === "object") ||
    (Array.isArray(taskDebug?.visualization3DPanels) &&
      taskDebug.visualization3DPanels.length > 0) ||
    (taskDebug?.landingPagePreview != null &&
      typeof taskDebug.landingPagePreview === "object") ||
    (taskDebug?.diagramPreview != null &&
      typeof taskDebug.diagramPreview === "object")
  );
};

export const isAssistantArtifactLongRunningWorkflow = (input: {
  taskClass?: string | null;
  workflowKey?: string | null;
}): boolean => {
  if (String(input.taskClass ?? "").trim() === "artifact_generate") {
    return true;
  }
  const workflowKey = String(input.workflowKey ?? "").trim();
  return (
    ASSISTANT_LONG_RUNNING_ARTIFACT_WORKFLOW_KEYS.has(workflowKey) ||
    isContractedArtifactWorkflow(workflowKey)
  );
};

export const projectAssistantArtifactLongRunningWorkflow = (
  input: AssistantArtifactLongRunningWorkflowInput,
): AssistantArtifactLongRunningWorkflowProjection => {
  const normalizedState = normalizeState(input.state);
  const phaseLabel =
    normalizeCopy(input.phaseLabel) ?? defaultPhaseLabel(normalizedState);
  const resultSummary = normalizeCopy(input.resultSummary);
  const statusMessage = normalizeCopy(input.statusMessage);
  const isArtifactWorkflow = isAssistantArtifactLongRunningWorkflow({
    taskClass: input.taskClass ?? null,
    workflowKey: input.workflowKey ?? null,
  });
  const hasArtifactPayload =
    Boolean(
      input.artifacts?.some(
        (artifact) =>
          Boolean(artifact?.downloadUrl) ||
          Boolean(artifact?.sourceRef) ||
          artifact?.artifactType === "chart" ||
          artifact?.artifactType === "spreadsheet" ||
          artifact?.artifactType === "spreadsheet-workbook" ||
          artifact?.artifactType === "html-page" ||
          artifact?.artifactType === "diagram" ||
          artifact?.artifactType === "report" ||
          artifact?.artifactType === "invoice",
      ),
    ) || hasArtifactPayloadFromResponse(input.response);
  const hasRequiredPreview = hasRequiredArtifactPreviewForWorkflow({
    workflowKey: input.workflowKey,
    artifactPreview: input.artifactPreview ?? null,
  });
  const requiresPreviewContract = requiresArtifactPreviewContract({
    workflowKey: input.workflowKey,
    taskClass: input.taskClass,
  });

  if (
    isArtifactWorkflow &&
    normalizedState === "completed" &&
    (!hasArtifactPayload || (requiresPreviewContract && !hasRequiredPreview))
  ) {
    return {
      state: ASSISTANT_ARTIFACT_HYDRATING_STATE,
      phaseLabel: "Finalizing artifact",
      statusMessage:
        statusMessage ??
        "Preparing the final preview and workspace so it appears here without a refresh.",
      progressPercent: Math.max(
        96,
        clampProgress(
          input.progressPercent,
          ASSISTANT_ARTIFACT_HYDRATING_STATE,
        ),
      ),
      resultSummary,
      hasArtifactPayload,
      isArtifactWorkflow,
      isHydratingArtifact: true,
      isReady: false,
    };
  }

  if (normalizedState === "completed") {
    return {
      state: normalizedState,
      phaseLabel: phaseLabel || "Completed",
      statusMessage:
        resultSummary ??
        statusMessage ??
        (isArtifactWorkflow && hasArtifactPayload ? "Artifact ready." : null),
      progressPercent: 100,
      resultSummary,
      hasArtifactPayload,
      isArtifactWorkflow,
      isHydratingArtifact: false,
      isReady:
        !isArtifactWorkflow ||
        (hasArtifactPayload &&
          (!requiresPreviewContract || hasRequiredPreview)),
    };
  }

  return {
    state: normalizedState,
    phaseLabel,
    statusMessage:
      statusMessage ??
      (isArtifactWorkflow
        ? "Working in the background. This thread will update as progress changes."
        : null),
    progressPercent: clampProgress(input.progressPercent, normalizedState),
    resultSummary,
    hasArtifactPayload,
    isArtifactWorkflow,
    isHydratingArtifact: false,
    isReady: false,
  };
};

export const toAssistantLongRunningWorkflowThreadState = (
  state: string,
): AssistantLongRunningWorkflowThreadState => {
  const normalized = normalizeState(state);
  if (
    normalized === "completed" ||
    normalized === "failed" ||
    normalized === "cancelled"
  ) {
    return normalized;
  }
  if (normalized === ASSISTANT_ARTIFACT_HYDRATING_STATE) {
    return ASSISTANT_ARTIFACT_HYDRATING_STATE;
  }
  if (normalized === "queued" || normalized === "started") {
    return "started";
  }
  return "running";
};

export const toAssistantLongRunningWorkflowProgressBucket = (
  progressPercent: number | null | undefined,
): number => {
  const value = clampProgress(progressPercent, "running");
  return Math.min(100, Math.floor(value / 10) * 10);
};

export const buildAssistantArtifactWorkflowProgressMessage = (input: {
  title: string;
  workflow: AssistantArtifactLongRunningWorkflowProjection;
}): string => {
  const title = normalizeCopy(input.title) ?? "Background task";
  const state = input.workflow.state;
  if (state === ASSISTANT_ARTIFACT_HYDRATING_STATE) {
    return `${title} finished the main work. Finalizing the artifact preview and workspace now so it appears here without a refresh.`;
  }
  if (state === "queued" || state === "started") {
    return `${title} started. I will keep it running in the background while you keep chatting.`;
  }
  const phaseLabel = input.workflow.phaseLabel
    ? input.workflow.phaseLabel.toLowerCase()
    : "running";
  const detail =
    input.workflow.statusMessage ??
    "I will keep updating this thread as progress changes.";
  return `${title} is ${phaseLabel}. ${detail}`;
};
